import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FeedbackForm from './FeedbackForm';
import './ClientFeedbackFormPage.css'; // Import CSS for custom styling
import TextField from '@mui/material/TextField';

function ClientFeedbackFormPage() {
    // --------------------------------------------
    // 1) Comment out the old clientId param usage:
    // const { clientId } = useParams();
    // --------------------------------------------

    const [clientDetails, setClientDetails] = useState(null);
    const [managers, setManagers] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(true);

    // Keep the slug usage for new route:
    const { slug } = useParams();

    useEffect(() => {
        const fetchClientDetails = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/clients.php?slug=${slug}`
                );
                const data = await response.json();

                if (response.ok && data) {
                    // If the data includes a name, it's likely a single client object
                    if (data.name) {
                        setClientDetails(data);
                        setManagers(data.managers || []);
                    }
                    // Otherwise, if it was multiple records but that’s no longer the case 
                    // for slug-based lookups. This block remains just for fallback:
                    else if (data.records?.length > 0) {
                        // If you still want to handle the old code that used clientId, 
                        // you can keep the line below commented:
                        // const client = data.records.find(client => client.id === parseInt(clientId, 10));
                        // setClientDetails(client || {});
                        // setManagers(client?.managers || []);
                        setError("Client details not found in records array.");
                    } else {
                        setError("Client details not found.");
                    }
                } else {
                    setError("Failed to load client details.");
                }
            } catch (error) {
                console.error("Error fetching client details:", error);
                setError("An error occurred while fetching client details.");
            } finally {
                setLoading(false);
            }
        };

        fetchClientDetails();

        // If you want to keep the dependency array referencing clientId for now,
        // just comment it out and replace it with slug:
        // }, [clientId]);
    }, [slug]);

    const handleNewFeedback = () => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
    };

    // Build background and logo URLs
    const backgroundImage = clientDetails?.background
        ? `${process.env.REACT_APP_BACKEND_URL}/h${clientDetails.background.replace('../', '')}`
        : '';
    const logoImage = clientDetails?.logo
        ? `${process.env.REACT_APP_BACKEND_URL}/${clientDetails.logo.replace('../', '')}`
        : '';

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="error-message">{error}</p>;
    if (!clientDetails) return <p>Client details not found.</p>;

    return (
        <div
            className="feedback-page-container"
            style={{
                backgroundImage: backgroundImage
                    ? `linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${backgroundImage})`
                    : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <div className="left-content">
                {logoImage && (
                    <div className="client-logo-container">
                        <img
                            src={logoImage}
                            alt={`${clientDetails.name} Logo`}
                            className="client-logo"
                        />
                    </div>
                )}
                <div className="feedback-form-header">
                    <h2>Your Voice Matters – Help Us Grow</h2>
                    <p>
                        We value your experience and insights, so we’re working to build trust through transparency.
                        Please provide contact info for follow-up. We look forward to connecting with you!
                    </p>
                </div>
            </div>
            {/* 
              2) Pass clientDetails.id to the FeedbackForm’s clientId prop. 
                 This ensures the POST body has a real numeric client_id. 
            */}
            <FeedbackForm
                clientId={clientDetails?.id}
                onNewFeedback={handleNewFeedback}
                managers={managers}
                slug={slug}
            />
        </div>
    );
}

export default ClientFeedbackFormPage;
